import type React from 'react';

import { TextField } from 'ui';
import type { TextFieldProps } from 'ui/TextField/types';

export const PRICE_FILTER_MIN = 0;
export const PRICE_FILTER_MAX = 100_000;

const stripDigits = (value: string): string => value.replace(/\D/g, '');

export const formatPriceAmount = (raw: string | number | null | undefined): string => {
  const digits = stripDigits(String(raw ?? ''));
  if (!digits) return '';
  const num = Number(digits);
  if (!Number.isFinite(num)) return '';
  return new Intl.NumberFormat('ru-RU').format(num);
};

const clampAmount = (digits: string, min?: number, max?: number): string => {
  if (!digits) return '';
  let num = Number(digits);
  if (!Number.isFinite(num)) return '';
  if (min != null && num < min) num = min;
  if (max != null && num > max) num = max;
  return String(num);
};

export type PriceAmountFieldProps = {
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  shape?: TextFieldProps['shape'];
  size?: TextFieldProps['size'];
  label?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

export const PriceAmountField: React.FC<PriceAmountFieldProps> = ({
  value,
  onChange,
  min,
  max,
  shape = 'brick',
  size = 'md',
  label,
  placeholder,
  className,
  style,
}) => {
  const displayValue = formatPriceAmount(value);

  const commit = (raw: string) => {
    const digits = stripDigits(raw);
    onChange(clampAmount(digits, min, max));
  };

  return (
    <TextField
      className={className}
      style={style}
      shape={shape}
      size={size}
      label={label}
      placeholder={placeholder}
      value={displayValue}
      onChange={commit}
      blurOnChange={commit}
      input={(className) => (
        <input
          className={className}
          type='tel'
          inputMode='numeric'
          autoComplete='off'
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => commit(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
        />
      )}
    />
  );
};

export type PriceRangeInputProps = {
  fromValue: string;
  toValue: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  min?: number;
  max?: number;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  shape?: TextFieldProps['shape'];
  size?: TextFieldProps['size'];
  className?: string;
  style?: React.CSSProperties;
  /** Заявка: границы независимы, минимум применяется когда поле заполнено. */
  independent?: boolean;
};

export const PriceRangeInput: React.FC<PriceRangeInputProps> = ({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  min = PRICE_FILTER_MIN,
  max = PRICE_FILTER_MAX,
  fromPlaceholder = 'От',
  toPlaceholder = 'До',
  shape,
  size,
  className,
  style,
  independent = false,
}) => {
  const handleFromChange = (next: string) => {
    onFromChange(next);
    if (independent) return;
    const fromNum = Number(next);
    const toNum = Number(toValue);
    if (next && toValue && fromNum > toNum) {
      onToChange(next);
    }
  };

  const handleToChange = (next: string) => {
    onToChange(next);
    if (independent) return;
    const fromNum = Number(fromValue);
    const toNum = Number(next);
    if (next && fromValue && toNum < fromNum) {
      onFromChange(next);
    }
  };

  return (
    <div className={className} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', ...style }}>
      <PriceAmountField
        value={fromValue}
        onChange={handleFromChange}
        min={independent ? undefined : min}
        max={max}
        shape={shape}
        size={size || 'lg'}
        label='От, руб/час'
        placeholder={fromPlaceholder}
      />
      <PriceAmountField
        value={toValue}
        onChange={handleToChange}
        min={independent ? undefined : min}
        max={max}
        shape={shape}
        size={size || 'lg'}
        label='До, руб/час'
        placeholder={toPlaceholder}
      />
    </div>
  );
};
